import { useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, LogIn, Play, Copy, Code2, ShieldCheck, Wallet, AlertTriangle, Smartphone, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/sections/ToolPageNavbar";
import Footer from "@/components/sections/Footer";
import { getStoredToken } from "@/utils/api";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const GROWTH_BODY = {
  name: "Aarav Sharma", gender: "male", chronological_age: 8.3, date_of_birth: "2018-06-10",
  current_height: 130, present_weight: 30, bone_age: 8.3,
  father_height: 176, mother_height: 158, ethnicity: "ind",
};
const NUTRITION_BODY = {
  name: "Aarav Sharma", gender: "male", chronological_age: 8.5, bone_age: 8.0,
  present_height: 125, present_weight: 25, expected_height: 130, expected_weight: 28,
  food_type: "veg", region: "South India",
};
const BONE_BODY = { name: "Aarav Sharma", sex: 1, age: 8.3, image_base64: "<base64-encoded hand X-ray PNG/JPG>" };

const ENDPOINTS = [
  { id: "me", method: "GET", path: "/me", title: "Who am I", desc: "Check your credentials. Returns the account, credit balance and whether the key is on the credit wallet or free." },
  { id: "growth", method: "POST", path: "/growth/assess", title: "Growth Assessment", desc: "Adult-height prediction (Bayley-Pinneau + mid-parental), IAP-2015 / WHO percentiles, BMI class and Growth Health Index. Without an X-ray, send bone_age equal to chronological_age.", body: GROWTH_BODY },
  { id: "bone", method: "POST", path: "/bone-age/predict", title: "Bone Age from X-ray", desc: "Send a left-hand X-ray as base64. sex: 1 = male, 0 = female. Returns predicted bone age and consistency with chronological age.", body: BONE_BODY },
  { id: "nutrition", method: "POST", path: "/nutrition/analyze", title: "NIN-NutriGuide", desc: "ICMR-NIN 2020 daily nutrient targets, regional foods and day / week / month meal plans. region: South India, North India, East India, West India, North East India or Central India. food_type: veg, non-veg or mixed.", body: NUTRITION_BODY },
  { id: "reports", method: "GET", path: "/reports?limit=50", title: "List past reports", desc: "Reports created by this account (newest first). School accounts receive aggregate counts only." },
  { id: "report", method: "GET", path: "/reports/{report_id}", title: "Get one report", desc: "Full stored input and result for a report_id." },
  { id: "pdf", method: "GET", path: "/reports/{report_id}/pdf", title: "Download PDF report", desc: "The same branded PDF as the website. Uses 1 credit the first time (free keys and re-downloads are free). Nutrition reports accept ?meal_plan_scope=none|day|week|month." },
];

const ERRORS = [
  ["401", "Missing, invalid or revoked API key / session"],
  ["402", "No report credits left. Top up at tots360.in/checkout"],
  ["403", "Your account role cannot access this data"],
  ["404", "Report not found"],
  ["422", "Invalid request body. The response lists each field problem"],
];

const snippet = (ep, lang) => {
  const url = `${BASE}${ep.path}`;
  const body = ep.body ? JSON.stringify(ep.body, null, 2) : null;
  if (lang === "curl") {
    return `curl -X ${ep.method} "${url}" \\\n  -H "X-API-Key: $TOTS360_API_KEY"${body ? ` \\\n  -H "Content-Type: application/json" \\\n  -d '${body}'` : ""}${ep.id === "pdf" ? " \\\n  -o report.pdf" : ""}`;
  }
  if (lang === "python") {
    return `import os, requests\n\nr = requests.${ep.method.toLowerCase()}(\n    "${url}",\n    headers={"X-API-Key": os.environ["TOTS360_API_KEY"]},${body ? `\n    json=${body.replace(/\n/g, "\n    ")},` : ""}\n)\nr.raise_for_status()\n${ep.id === "pdf" ? 'open("report.pdf", "wb").write(r.content)' : "print(r.json())"}`;
  }
  return `const res = await fetch("${url}", {\n  method: "${ep.method}",\n  headers: {\n    "X-API-Key": process.env.TOTS360_API_KEY,${body ? '\n    "Content-Type": "application/json",' : ""}\n  },${body ? `\n  body: JSON.stringify(${body.replace(/\n/g, "\n  ")}),` : ""}\n});\n${ep.id === "pdf" ? "const pdf = await res.arrayBuffer();" : "console.log(await res.json());"}`;
};

const CodeBlock = ({ code, testId }) => (
  <div className="relative">
    <button onClick={async () => { try { await navigator.clipboard.writeText(code); toast.success("Copied"); } catch { toast.error("Couldn't copy. Please select and copy manually."); } }}
      className="absolute top-2 right-2 p-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white/60" data-testid={`${testId}-copy`} aria-label="Copy code">
      <Copy className="w-3.5 h-3.5" />
    </button>
    <pre className="text-xs leading-relaxed text-[#C8F5F1] bg-[#060B1E] border border-white/10 rounded-xl p-4 pr-10 overflow-x-auto" data-testid={testId}><code>{code}</code></pre>
  </div>
);

const MethodTag = ({ m }) => (
  <span className={`font-mono text-[11px] font-bold px-2 py-0.5 rounded ${m === "GET" ? "bg-[#4ECDC4]/15 text-[#4ECDC4]" : "bg-[#FFE66D]/15 text-[#FFE66D]"}`}>{m}</span>
);

const Hero = () => (
  <section className="relative pt-32 pb-14 md:pt-40 px-6 overflow-hidden">
    <div className="absolute -top-32 -right-32 w-[460px] h-[460px] rounded-full bg-[#4ECDC4]/12 blur-3xl pointer-events-none" />
    <div className="relative max-w-6xl mx-auto">
      <div className="badge-glass inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium mb-6">
        <Code2 className="w-3.5 h-3.5 text-[#4ECDC4]" /> tots360 API · v1
      </div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-5" data-testid="dev-title">
        Bring tots360 into <span className="text-[#4ECDC4]">your own software.</span>
      </h1>
      <p className="text-white/60 text-base md:text-lg max-w-2xl mb-8">
        Hospitals, schools, labs and the tots360 mobile app use the same API as our website: growth assessment,
        X-ray bone age, NIN-NutriGuide nutrition and branded PDF reports.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs uppercase tracking-[0.15em] text-white/40">Base URL</span>
        <code className="text-sm text-white bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 break-all" data-testid="dev-base-url">{BASE}</code>
      </div>
    </div>
  </section>
);

const AuthCards = () => (
  <section className="px-6 pb-16">
    <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-5">
      <div className="glass-card rounded-3xl p-6 md:p-8 min-w-0" data-testid="dev-auth-api-key">
        <Building2 className="w-6 h-6 text-[#4ECDC4] mb-4" />
        <h2 className="font-display text-xl text-white mb-2">Partners: secret API key</h2>
        <p className="text-sm text-white/60 mb-4">The tots360 team issues a key linked to your partner account. Send it on every request from your server. Never put it in a website or mobile app.</p>
        <CodeBlock code={'X-API-Key: t360_live_xxxxxxxxxxxx.xxxxxxxx'} testId="dev-auth-key-code" />
      </div>
      <div className="glass-card rounded-3xl p-6 md:p-8 min-w-0" data-testid="dev-auth-session">
        <Smartphone className="w-6 h-6 text-[#FFE66D] mb-4" />
        <h2 className="font-display text-xl text-white mb-2">Apps: sign in with a tots360 account</h2>
        <p className="text-sm text-white/60 mb-4">Log in with email and password, then send the returned session_token as a Bearer token (valid 7 days).</p>
        <CodeBlock testId="dev-auth-session-code" code={`POST ${process.env.REACT_APP_BACKEND_URL}/api/auth/login\n{"email": "you@clinic.in", "password": "••••••"}\n\nAuthorization: Bearer <session_token>`} />
      </div>
    </div>
  </section>
);

const EndpointCard = ({ ep }) => {
  const [lang, setLang] = useState("curl");
  return (
    <div className="glass-card rounded-3xl p-6 md:p-8 min-w-0" id={`ep-${ep.id}`} data-testid={`dev-endpoint-${ep.id}`}>
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <MethodTag m={ep.method} />
        <code className="text-sm text-white break-all">{ep.path}</code>
      </div>
      <h3 className="font-display text-lg text-white mb-1">{ep.title}</h3>
      <p className="text-sm text-white/55 mb-5 max-w-3xl">{ep.desc}</p>
      <div className="flex gap-2 mb-3">
        {["curl", "python", "javascript"].map((l) => (
          <button key={l} onClick={() => setLang(l)} data-testid={`dev-endpoint-${ep.id}-lang-${l}`}
            className={`px-3 py-1 rounded-full text-xs border transition-colors ${lang === l ? "border-[#4ECDC4] text-[#4ECDC4] bg-[#4ECDC4]/10" : "border-white/10 text-white/50 hover:text-white"}`}>
            {l === "javascript" ? "JavaScript" : l === "python" ? "Python" : "cURL"}
          </button>
        ))}
      </div>
      <CodeBlock code={snippet(ep, lang)} testId={`dev-endpoint-${ep.id}-code`} />
    </div>
  );
};

const TRY_OPTIONS = ENDPOINTS.filter((e) => ["me", "growth", "nutrition", "reports"].includes(e.id));

const TryIt = () => {
  const [mode, setMode] = useState("key");
  const [key, setKey] = useState("");
  const [epId, setEpId] = useState("me");
  const ep = TRY_OPTIONS.find((e) => e.id === epId);
  const [body, setBody] = useState("");
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);

  const pick = (id) => {
    setEpId(id);
    const e = TRY_OPTIONS.find((x) => x.id === id);
    setBody(e.body ? JSON.stringify(e.body, null, 2) : "");
    setOut(null);
  };

  const run = async () => {
    const headers = { "Content-Type": "application/json" };
    if (mode === "key") headers["X-API-Key"] = key.trim();
    else headers.Authorization = `Bearer ${getStoredToken() || ""}`;
    setBusy(true);
    try {
      const res = await fetch(`${BASE}${ep.path}`, { method: ep.method, headers, body: ep.body ? body : undefined, credentials: "omit" });
      const text = await res.text();
      let pretty = text;
      try { pretty = JSON.stringify(JSON.parse(text), null, 2); } catch { /* not JSON */ }
      setOut({ status: res.status, text: pretty.length > 6000 ? `${pretty.slice(0, 6000)}\n… (truncated)` : pretty });
    } catch (e) {
      setOut({ status: "error", text: String(e) });
    } finally { setBusy(false); }
  };

  const loggedIn = !!getStoredToken();
  return (
    <section id="try-it" className="px-6 py-16" data-testid="dev-try-it">
      <div className="max-w-6xl mx-auto glass-card rounded-3xl p-6 md:p-10">
        <div className="flex items-center gap-2 mb-2"><Play className="w-5 h-5 text-[#FF6B6B]" /><h2 className="font-display text-2xl text-white">Try it live</h2></div>
        <p className="text-sm text-white/55 mb-6">Calls run against your real account and appear in your reports. Your key is used only for this request and never saved.</p>
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="space-y-4 min-w-0">
            <div className="flex gap-2">
              <button onClick={() => setMode("key")} data-testid="dev-try-mode-key" className={`px-3 py-1.5 rounded-full text-xs border inline-flex items-center gap-1.5 ${mode === "key" ? "border-[#4ECDC4] text-[#4ECDC4] bg-[#4ECDC4]/10" : "border-white/10 text-white/50"}`}><KeyRound className="w-3.5 h-3.5" /> API key</button>
              <button onClick={() => setMode("session")} data-testid="dev-try-mode-session" className={`px-3 py-1.5 rounded-full text-xs border inline-flex items-center gap-1.5 ${mode === "session" ? "border-[#4ECDC4] text-[#4ECDC4] bg-[#4ECDC4]/10" : "border-white/10 text-white/50"}`}><LogIn className="w-3.5 h-3.5" /> My login</button>
            </div>
            {mode === "key" ? (
              <input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="t360_live_…" autoComplete="off" data-testid="dev-try-key-input"
                className="w-full px-4 py-2.5 rounded-xl bg-[#060B1E] border border-white/10 text-white text-sm focus:border-[#4ECDC4] outline-none" />
            ) : (
              <p className="text-sm text-white/60" data-testid="dev-try-session-status">
                {loggedIn ? "Using your current tots360 login." : <>You're not logged in. <Link to="/login" className="text-[#4ECDC4] underline">Log in</Link> first.</>}
              </p>
            )}
            <select value={epId} onChange={(e) => pick(e.target.value)} data-testid="dev-try-endpoint-select"
              className="w-full px-4 py-2.5 rounded-xl bg-[#060B1E] border border-white/10 text-white text-sm outline-none">
              {TRY_OPTIONS.map((e) => <option key={e.id} value={e.id}>{e.method} {e.path} · {e.title}</option>)}
            </select>
            {ep.body && (
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} spellCheck={false} data-testid="dev-try-body-input"
                className="w-full font-mono text-xs px-4 py-3 rounded-xl bg-[#060B1E] border border-white/10 text-[#C8F5F1] outline-none focus:border-[#4ECDC4]" />
            )}
            <button onClick={run} disabled={busy || (mode === "key" && !key.trim())} data-testid="dev-try-send-btn"
              className="btn-coral px-6 py-2.5 rounded-full font-semibold inline-flex items-center gap-2 disabled:opacity-50">
              <Play className="w-4 h-4" /> {busy ? "Sending…" : "Send request"}
            </button>
          </div>
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.15em] text-white/40 mb-2">Response {out && <span data-testid="dev-try-status" className={`ml-2 font-mono ${String(out.status).startsWith("2") ? "text-emerald-300" : "text-rose-300"}`}>{out.status}</span>}</p>
            <pre className="text-xs leading-relaxed text-white/80 bg-[#060B1E] border border-white/10 rounded-xl p-4 h-[420px] overflow-auto whitespace-pre-wrap break-all" data-testid="dev-try-response">{out ? out.text : "Send a request to see the response here."}</pre>
          </div>
        </div>
      </div>
    </section>
  );
};

const Billing = () => (
  <section className="px-6 pb-16">
    <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-5">
      <div className="glass-card rounded-3xl p-6 md:p-8 min-w-0" data-testid="dev-billing">
        <Wallet className="w-6 h-6 text-[#FFE66D] mb-4" />
        <h2 className="font-display text-xl text-white mb-3">Billing</h2>
        <ul className="space-y-2 text-sm text-white/65">
          <li>Analyses (growth, bone age, nutrition) and report lookups have no charge.</li>
          <li>Each new PDF report uses <strong className="text-white">1 credit</strong> from the account's wallet. Re-downloading the same report is free.</li>
          <li>Approved partners can be given a <strong className="text-white">free key</strong> with no credit charges.</li>
        </ul>
      </div>
      <div className="glass-card rounded-3xl p-6 md:p-8 min-w-0" data-testid="dev-errors">
        <AlertTriangle className="w-6 h-6 text-[#FF6B6B] mb-4" />
        <h2 className="font-display text-xl text-white mb-3">Errors</h2>
        <div className="divide-y divide-white/5">
          {ERRORS.map(([c, d]) => (
            <div key={c} className="flex gap-4 py-2 text-sm"><code className="text-[#FF6B6B] w-10">{c}</code><span className="text-white/60">{d}</span></div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const GetAccess = () => (
  <section className="px-6 pb-20">
    <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center gap-6 rounded-3xl border border-white/10 bg-white/[0.02] p-6 md:p-8" data-testid="dev-get-access">
      <ShieldCheck className="w-8 h-8 text-[#4ECDC4] shrink-0" />
      <div className="flex-1">
        <h2 className="font-display text-xl text-white mb-1">Get a partner key</h2>
        <p className="text-sm text-white/60">Create a tots360 account (doctor or school), then write to info@tots360.in or call +91 8985709090. We'll issue a key linked to your account.</p>
      </div>
      <Link to="/signup" className="btn-glass px-6 py-2.5 rounded-full font-semibold text-white whitespace-nowrap" data-testid="dev-signup-btn">Create account</Link>
    </div>
  </section>
);

export default function DevelopersPage() {
  return (
    <div className="min-h-screen bg-[#0A1029] text-white" data-testid="developers-page">
      <Navbar />
      <Hero />
      <AuthCards />
      <section className="px-6 pb-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-3xl text-white mb-6">Endpoints</h2>
          <div className="space-y-5">{ENDPOINTS.map((ep) => <EndpointCard key={ep.id} ep={ep} />)}</div>
        </div>
      </section>
      <TryIt />
      <Billing />
      <GetAccess />
      <Footer />
      <Toaster position="top-right" />
    </div>
  );
}
